import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Cookie, Settings, X } from "lucide-react";

const COOKIE_CONSENT_KEY = "hak-cookie-consent";
const COOKIE_PREFERENCES_KEY = "hak-cookie-preferences";

const defaultPreferences = {
  necessary: true, // Always true, not changeable
  analytics: false,
  marketing: false,
  functional: false,
};

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    
    if (savedPrefs) {
      setPreferences(JSON.parse(savedPrefs));
    }

    // Show banner if no consent given
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const savePreferences = (prefs = preferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowModal(false);
    
    // Here you would typically trigger your analytics/marketing scripts
    if (prefs.analytics) {
      // Enable analytics
      console.log("Analytics enabled");
    }
    if (prefs.marketing) {
      // Enable marketing cookies
      console.log("Marketing enabled");
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    savePreferences(defaultPreferences);
  };

  const togglePreference = (key) => {
    if (key === "necessary") return; // Can't toggle necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Always render the settings button, even if banner is hidden
  const renderCookieSettingsButton = () => (
    <button
      onClick={() => setShowModal(true)}
      className="fixed bottom-4 left-4 z-40 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-full text-xs border border-gray-300 shadow-sm transition-colors"
      title="Cookie-Einstellungen ändern"
    >
      <Cookie className="h-4 w-4 inline mr-1" />
      Cookies
    </button>
  );

  if (!showBanner) {
    return (
      <>
        {renderCookieSettingsButton()}
        {/* Cookie Settings Modal - still available even when banner is hidden */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5" />
                Cookie-Einstellungen
              </DialogTitle>
              <DialogDescription>
                Verwalte deine Cookie-Präferenzen. Du kannst diese jederzeit ändern.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Notwendige Cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Analyse-Cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => togglePreference("analytics")}
                    className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                      preferences.analytics ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
                    } px-1`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Marketing-Cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Diese Cookies werden für Werbezwecke und zur Personalisierung von Inhalten verwendet.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => togglePreference("marketing")}
                    className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                      preferences.marketing ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
                    } px-1`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Funktionale Cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung.
                  </p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => togglePreference("functional")}
                    className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                      preferences.functional ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
                    } px-1`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              {/* Legal Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Rechtliche Hinweise & Datenschutz</h4>
                <p className="text-xs text-gray-600 mb-2">
                  Gemäß DSGVO und TTDSG verarbeiten wir personenbezogene Daten nur mit deiner Einwilligung oder auf Basis berechtigter Interessen. 
                  Notwendige Cookies basieren auf Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse für Webseitenfunktionalität).
                </p>
                <p className="text-xs text-gray-600 mb-2">
                  <strong>Deine Rechte:</strong> Du kannst deine Einwilligung jederzeit widerrufen, Auskunft über gespeicherte Daten verlangen, 
                  sowie Berichtigung oder Löschung deiner Daten fordern.
                </p>
                <p className="text-xs text-gray-600">
                  <strong>Kontakt:</strong> Bei Fragen zu Cookies und Datenschutz kontaktiere uns unter{" "}
                  <a href="mailto:info@haksportcars-gutachten.de" className="text-blue-600 underline">
                    info@haksportcars-gutachten.de
                  </a>{" "}
                  oder siehe unsere{" "}
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-blue-600 underline"
                  >
                    Datenschutzerklärung
                  </button>.
                </p>
              </div>
            </div>

            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={acceptNecessary}
                variant="outline"
                className="text-sm"
              >
                Nur notwendige
              </Button>
              <Button
                onClick={() => savePreferences()}
                className="bg-[var(--brand-red)] hover:bg-red-600 text-white text-sm"
              >
                Einstellungen speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1">Cookie-Einstellungen</p>
                <p>
                  Wir verwenden Cookies, um dir die beste Erfahrung auf unserer Website zu bieten. 
                  Notwendige Cookies sind für die Funktion der Website erforderlich. Optional können Sie auch Analyse- und Marketing-Cookies aktivieren.{" "}
                  <button
                    onClick={() => setShowModal(true)}
                    className="underline hover:no-underline text-blue-600"
                  >
                    Mehr erfahren & Einstellungen
                  </button>
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Button
                onClick={acceptNecessary}
                variant="outline"
                size="sm"
                className="text-sm"
              >
                Nur notwendige
              </Button>
              <Button
                onClick={() => setShowModal(true)}
                variant="outline"
                size="sm"
                className="text-sm"
              >
                <Settings className="h-4 w-4 mr-2" />
                Einstellungen
              </Button>
              <Button
                onClick={acceptAll}
                size="sm"
                className="bg-[var(--brand-red)] hover:bg-red-600 text-white text-sm"
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5" />
              Cookie-Einstellungen
            </DialogTitle>
            <DialogDescription>
              Verwalte deine Cookie-Präferenzen. Du kannst diese jederzeit ändern.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Notwendige Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                </p>
              </div>
              <div className="ml-4">
                <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Analyse-Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => togglePreference("analytics")}
                  className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                    preferences.analytics ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
                  } px-1`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Marketing-Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Diese Cookies werden für Werbezwecke und zur Personalisierung von Inhalten verwendet.
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => togglePreference("marketing")}
                  className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                    preferences.marketing ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
                  } px-1`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Funktionale Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung.
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => togglePreference("functional")}
                  className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                    preferences.functional ? "bg-green-500 justify-end" : "bg-gray-300 justify-start"
                  } px-1`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
            </div>

            {/* Legal Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Rechtliche Hinweise & Datenschutz</h4>
              <p className="text-xs text-gray-600 mb-2">
                Gemäß DSGVO und TTDSG verarbeiten wir personenbezogene Daten nur mit deiner Einwilligung oder auf Basis berechtigter Interessen. 
                Notwendige Cookies basieren auf Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse für Webseitenfunktionalität).
              </p>
              <p className="text-xs text-gray-600 mb-2">
                <strong>Deine Rechte:</strong> Du kannst deine Einwilligung jederzeit widerrufen, Auskunft über gespeicherte Daten verlangen, 
                sowie Berichtigung oder Löschung deiner Daten fordern.
              </p>
              <p className="text-xs text-gray-600">
                <strong>Kontakt:</strong> Bei Fragen zu Cookies und Datenschutz kontaktiere uns unter{" "}
                <a href="mailto:info@haksportcars-gutachten.de" className="text-blue-600 underline">
                  info@haksportcars-gutachten.de
                </a>{" "}
                oder siehe unsere{" "}
                <button
                  onClick={() => setShowModal(false)}
                  className="text-blue-600 underline"
                >
                  Datenschutzerklärung
                </button>.
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={acceptNecessary}
              variant="outline"
              className="text-sm"
            >
              Nur notwendige
            </Button>
            <Button
              onClick={() => savePreferences()}
              className="bg-[var(--brand-red)] hover:bg-red-600 text-white text-sm"
            >
              Einstellungen speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
